import { z } from 'zod';
import { ActorKind, Visibility } from './enums.js';
import { ContentLocale, IsoDateTime, PageQuery, Title, UserRef, Uuid, UuidList, Version, boundedList } from './common.js';
import { CustomFieldValue } from './fields.js';
import { AccessGrantInput, AccessGrantView } from './identity.js';

/** Request body limit for document routes; the content schema itself caps documents at 2,000,000 bytes (ADR-0040). */
export const DOCUMENT_BODY_LIMIT = '2560kb';

export const FolderEditMode = z.enum(['INHERIT', 'VIEWERS', 'LISTED']);
export type FolderEditMode = z.infer<typeof FolderEditMode>;
export const DocumentRevisionKind = z.enum(['CREATE', 'EDIT', 'RESTORE']);
export type DocumentRevisionKind = z.infer<typeof DocumentRevisionKind>;

/** One emoji (with its modifiers) shown next to a folder or document name (ADR-0041). */
export const DocumentIcon = z
  .string()
  .min(1)
  .max(16)
  .refine((v) => /\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(v) && !/[\p{L}\p{N}<>"'`]/u.test(v.replace(/\uFE0F|\u200D/g, '')), { message: 'must be an emoji' });

const FolderName = z
  .string()
  .min(1)
  .max(120)
  .refine((v) => v.trim().length > 0, { message: 'must not be blank' });

/** ProseMirror JSON of the shared document schema; the server validates it node by node. */
export const DocumentContent = z
  .object({ type: z.literal('doc'), content: z.array(z.unknown()).optional() })
  .loose()
  .describe('ProseMirror JSON document ({ "type": "doc", "content": [...] })');
export type DocumentContent = z.infer<typeof DocumentContent>;

export const PathEntry = z.object({ id: Uuid, name: z.string() });
export type PathEntry = z.infer<typeof PathEntry>;

/** The edit rule that applies to a folder and the folder it comes from. */
export const EffectiveEditors = z.object({
  mode: z.enum(['VIEWERS', 'LISTED']),
  folderId: Uuid,
  folderName: z.string(),
  editors: z.array(AccessGrantView),
});
export type EffectiveEditors = z.infer<typeof EffectiveEditors>;

/** System folders (ADR-0056): `PUBLIC_EXTERNAL` is the only public root, "Public external" in the UI. */
export const DocumentFolderSystemKind = z.enum(['PUBLIC_EXTERNAL']);
export type DocumentFolderSystemKind = z.infer<typeof DocumentFolderSystemKind>;

/** One readable folder in the tree. */
export const FolderSummary = z.object({
  id: Uuid,
  parentId: Uuid.nullable(),
  name: z.string(),
  icon: z.string().nullable(),
  position: z.number().int(),
  version: z.number().int(),
  visibility: Visibility,
  /** Whether this folder or an ancestor restricts its audience. */
  restricted: z.boolean(),
  canEdit: z.boolean(),
  documentCount: z.number().int(),
  /** Set on system folders; null on every folder people create. */
  systemKind: DocumentFolderSystemKind.nullable(),
  /** Inside the "Public external" tree: its unlocked documents have anonymous public links (ADR-0056). */
  publicTree: z.boolean(),
});
export type FolderSummary = z.infer<typeof FolderSummary>;

export const FolderView = FolderSummary.extend({
  editMode: FolderEditMode,
  /** The folder's own lists; viewers only apply when it is RESTRICTED. */
  viewers: z.array(AccessGrantView),
  editors: z.array(AccessGrantView),
  effectiveEditors: EffectiveEditors,
  path: z.array(PathEntry),
  archivedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  availableActions: z.array(z.string()),
});
export type FolderView = z.infer<typeof FolderView>;

export const CreateFolder = z
  .object({
    parentId: Uuid.optional(),
    name: FolderName,
    icon: DocumentIcon.optional(),
    visibility: Visibility.optional(),
    viewers: boundedList(AccessGrantInput).optional(),
    editMode: FolderEditMode.optional(),
    editors: boundedList(AccessGrantInput).optional(),
  })
  .strict();
export type CreateFolder = z.infer<typeof CreateFolder>;

export const UpdateFolder = z
  .object({
    expectedVersion: Version,
    name: FolderName.optional(),
    icon: DocumentIcon.nullable().optional(),
    /** Move under another folder; null makes it a top-level folder. */
    parentId: Uuid.nullable().optional(),
    position: z.number().int().min(0).max(1_000_000).optional(),
    archived: z.boolean().optional(),
    /** Confirms that a move into the "Public external" folder lets anyone with a link read its documents (ADR-0056). */
    confirmPublic: z.boolean().optional(),
  })
  .strict();
export type UpdateFolder = z.infer<typeof UpdateFolder>;

export const SetFolderAccess = z
  .object({
    expectedVersion: Version,
    visibility: Visibility,
    viewers: boundedList(AccessGrantInput),
    editMode: FolderEditMode,
    editors: boundedList(AccessGrantInput),
  })
  .strict();
export type SetFolderAccess = z.infer<typeof SetFolderAccess>;

export const DocumentSummary = z.object({
  id: Uuid,
  folderId: Uuid,
  /** Subpages (ADR-0042) sit under another document of the same folder. */
  parentDocumentId: Uuid.nullable(),
  title: z.string(),
  icon: z.string().nullable(),
  position: z.number().int(),
  version: z.number().int(),
  locked: z.boolean(),
  archivedAt: IsoDateTime.nullable(),
  updatedAt: IsoDateTime,
  updatedBy: UserRef.nullable(),
});
export type DocumentSummary = z.infer<typeof DocumentSummary>;

export const DocumentListQuery = PageQuery.extend({ includeArchived: z.enum(['true', 'false']).optional() });

export const DocumentView = z.object({
  id: Uuid,
  folderId: Uuid,
  path: z.array(PathEntry),
  parentDocumentId: Uuid.nullable(),
  /** Parent documents of a subpage, outermost first. */
  parentPath: z.array(PathEntry),
  title: z.string(),
  icon: z.string().nullable(),
  content: DocumentContent,
  /** Document property values by property id (ADR-0042). */
  properties: z.record(z.string(), CustomFieldValue),
  originalLocale: z.string(),
  position: z.number().int(),
  locked: z.boolean(),
  lockedAt: IsoDateTime.nullable(),
  /** People who see a locked draft (the creator and the people they named). */
  lockMembers: z.array(AccessGrantView),
  /** Named people who cannot see the folder, so the lock does not let them in (a lock only narrows). */
  lockMembersWithoutFolderAccess: z.array(Uuid),
  version: z.number().int(),
  contentRevision: z.number().int(),
  createdBy: UserRef.nullable(),
  updatedBy: UserRef.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: IsoDateTime.nullable(),
  /** Inside the "Public external" folder tree (ADR-0056). */
  publicTree: z.boolean(),
  /** Anyone with its public link reads it: in the public tree, not locked (nor under a locked page) and not archived. */
  isPublic: z.boolean(),
  availableActions: z.array(z.string()),
});
export type DocumentView = z.infer<typeof DocumentView>;

export const CreateDocument = z
  .object({
    folderId: Uuid,
    /** Create it as a subpage of this document (same folder). */
    parentDocumentId: Uuid.optional(),
    title: Title,
    icon: DocumentIcon.optional(),
    content: DocumentContent.optional(),
    /** Start as a private draft visible only to its creator. */
    locked: z.boolean().optional(),
    originalLocale: ContentLocale.optional(),
  })
  .strict();
export type CreateDocument = z.infer<typeof CreateDocument>;

export const UpdateDocument = z
  .object({
    expectedVersion: Version,
    title: Title.optional(),
    icon: DocumentIcon.nullable().optional(),
    content: DocumentContent.optional(),
    originalLocale: ContentLocale.optional(),
    folderId: Uuid.optional(),
    /** Move under another document (it takes that document's folder), or null for the folder's top level. */
    parentDocumentId: Uuid.nullable().optional(),
    position: z.number().int().min(0).max(1_000_000).optional(),
    archived: z.boolean().optional(),
    /** Confirms that this change lets anyone with the public link read the document (ADR-0056). */
    confirmPublic: z.boolean().optional(),
  })
  .strict();
export type UpdateDocument = z.infer<typeof UpdateDocument>;

export const SetDocumentLock = z
  .object({
    expectedVersion: Version,
    locked: z.boolean(),
    /** People besides the creator who see the draft; they still need the folder's view access. */
    userIds: boundedList(Uuid),
    /** Confirms that unlocking a document of the "Public external" folder publishes it (ADR-0056). */
    confirmPublic: z.boolean().optional(),
  })
  .strict();
export type SetDocumentLock = z.infer<typeof SetDocumentLock>;

export const DocumentRevisionSummary = z.object({
  id: Uuid,
  revision: z.number().int(),
  kind: DocumentRevisionKind,
  title: z.string(),
  author: UserRef.nullable(),
  actorKind: ActorKind,
  client: z.string().nullable(),
  restoredFrom: z.number().int().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type DocumentRevisionSummary = z.infer<typeof DocumentRevisionSummary>;

export const DocumentHistory = z.object({
  revisions: z.array(DocumentRevisionSummary),
  /** Reads by administrators who would not see the document without the administrator role. */
  administratorAccesses: z.array(z.object({ actor: UserRef.nullable(), occurredAt: IsoDateTime })),
});
export type DocumentHistory = z.infer<typeof DocumentHistory>;

export const DocumentRevisionView = DocumentRevisionSummary.extend({ content: DocumentContent, markdown: z.string() });
export type DocumentRevisionView = z.infer<typeof DocumentRevisionView>;

export const RestoreDocumentRevision = z.object({ expectedVersion: Version }).strict();
export type RestoreDocumentRevision = z.infer<typeof RestoreDocumentRevision>;

/** Base64 of Yjs binary updates (ADR-0051). */
const Base64 = (max: number) => z.string().max(max).regex(/^[A-Za-z0-9+/]*={0,2}$/);
/**
 * One co-editing round trip (ADR-0051). `epoch` and `cursor` come from the previous answer (null on
 * open); `update` carries this editor's new Yjs changes; `awareness` its caret and name.
 */
export const CollabSync = z
  .object({
    epoch: z.number().int().min(0).nullable(),
    cursor: z.string().regex(/^\d{1,19}$/).nullable(),
    // Below the JSON body limit for documents (2,560 KB), so too large answers 422, not 413.
    update: Base64(2_000_000).optional(),
    awareness: Base64(32_000).optional(),
  })
  .strict();
export type CollabSync = z.infer<typeof CollabSync>;
/** Everyone else's updates since the cursor (merged), their awareness, and the document's version and title. `reset`: reload the editor. */
export const CollabSyncView = z.object({
  epoch: z.number().int(),
  cursor: z.string(),
  updates: z.array(z.string()),
  awareness: z.array(z.string()),
  version: z.number().int(),
  title: z.string(),
  reset: z.boolean(),
  canEdit: z.boolean(),
});
export type CollabSyncView = z.infer<typeof CollabSyncView>;

export const PresenceMode = z.enum(['VIEWING', 'EDITING']);
export const ReportPresence = z.object({ mode: PresenceMode }).strict();
export type ReportPresence = z.infer<typeof ReportPresence>;
/** Other people with the document open in the last 30 seconds (ADR-0041). */
export const DocumentPresenceView = z.object({ viewing: z.array(UserRef), editing: z.array(UserRef) });
export type DocumentPresenceView = z.infer<typeof DocumentPresenceView>;

export const DocumentSearchQuery = z.object({ q: z.string().min(1).max(200), limit: z.string().regex(/^\d{1,2}$/).optional() });
/** `snippet`: a short excerpt of the content around the matched words, each wrapped in U+0002 … U+0003, when the title did not match. */
export const DocumentSearchHit = z.object({ id: Uuid, title: z.string(), icon: z.string().nullable(), folderId: Uuid, path: z.string(), snippet: z.string().nullable() });
export type DocumentSearchHit = z.infer<typeof DocumentSearchHit>;
/** What a document's content points at (ADR-0040 §7). */
export const DocumentLinkTarget = z.enum(['DOCUMENT', 'WORK_ITEM', 'REQUEST', 'PROJECT', 'TEAM', 'USER']);
export const DocumentBacklinksQuery = z.object({ targetKind: DocumentLinkTarget, targetId: Uuid });

/** Archived folders and documents the person can read, newest first. */
export const ArchivedEntry = z.object({
  kind: z.enum(['FOLDER', 'DOCUMENT']),
  id: Uuid,
  name: z.string(),
  icon: z.string().nullable(),
  folderId: Uuid.nullable(),
  path: z.array(PathEntry),
  version: z.number().int(),
  archivedAt: IsoDateTime,
  canRestore: z.boolean(),
});
export type ArchivedEntry = z.infer<typeof ArchivedEntry>;

/** Mention a person by email; a company email nobody uses yet gets a person who claims it at first sign-in (ADR-0015, ADR-0041). */
export const MentionPerson = z.object({ email: z.email().max(254) }).strict();
export type MentionPerson = z.infer<typeof MentionPerson>;

export const DocumentMarkdown = z.object({ filename: z.string(), markdown: z.string() });
export type DocumentMarkdown = z.infer<typeof DocumentMarkdown>;

const CommentBody = z
  .string()
  .min(1)
  .max(8000)
  .refine((v) => v.trim().length > 0, { message: 'must not be blank' });

export const ThreadCommentView = z.object({
  id: Uuid,
  bodyMarkdown: z.string(),
  author: UserRef.nullable(),
  createdAt: IsoDateTime,
  editedAt: IsoDateTime.nullable(),
  canEdit: z.boolean(),
});
export type ThreadCommentView = z.infer<typeof ThreadCommentView>;

/** A comment thread anchored to a passage by its text (quote plus a little context on each side). */
export const DocumentThreadView = z.object({
  id: Uuid,
  quote: z.string(),
  prefix: z.string(),
  suffix: z.string(),
  createdBy: UserRef.nullable(),
  createdAt: IsoDateTime,
  resolvedAt: IsoDateTime.nullable(),
  resolvedBy: UserRef.nullable(),
  version: z.number().int(),
  comments: z.array(ThreadCommentView),
  canResolve: z.boolean(),
});
export type DocumentThreadView = z.infer<typeof DocumentThreadView>;

export const CreateDocumentThread = z
  .object({
    /** Empty for a comment on the whole document. */
    quote: z.string().max(2000).optional(),
    prefix: z.string().max(200).optional(),
    suffix: z.string().max(200).optional(),
    bodyMarkdown: CommentBody,
  })
  .strict();
export type CreateDocumentThread = z.infer<typeof CreateDocumentThread>;

export const ReplyDocumentThread = z.object({ bodyMarkdown: CommentBody }).strict();
export type ReplyDocumentThread = z.infer<typeof ReplyDocumentThread>;

export const ResolveDocumentThread = z.object({ expectedVersion: Version, resolved: z.boolean() }).strict();
export type ResolveDocumentThread = z.infer<typeof ResolveDocumentThread>;

// ---------------------------------------------------------------- ADR-0042

export const DocumentTitlesQuery = z.object({ ids: UuidList });
/** Current titles for page chips; documents the reader cannot see are left out. */
export const DocumentTitle = z.object({ id: Uuid, title: z.string(), icon: z.string().nullable(), archived: z.boolean() });
export type DocumentTitle = z.infer<typeof DocumentTitle>;

export const DocumentReaderView = z.object({ user: UserRef, firstViewedAt: IsoDateTime, lastViewedAt: IsoDateTime, viewCount: z.number().int() });
export type DocumentReaderView = z.infer<typeof DocumentReaderView>;

export const SetDocumentProperties = z.object({ values: z.record(Uuid, CustomFieldValue.nullable()) }).strict();
export type SetDocumentProperties = z.infer<typeof SetDocumentProperties>;

/** One filter of the documents table: equals (selects, yes/no), contains (text), empty or set. */
export const DocumentIndexFilter = z.object({ fieldId: Uuid, op: z.enum(['eq', 'contains', 'empty', 'set']), value: z.union([z.string(), z.number(), z.boolean()]).optional() }).strict();
export type DocumentIndexFilter = z.infer<typeof DocumentIndexFilter>;
export const DocumentIndexQuery = z.object({
  folderId: Uuid.optional(),
  q: z.string().max(200).optional(),
  /** Documents this person created or edited last (ADR-0045). */
  person: Uuid.optional(),
  /** JSON array of DocumentIndexFilter (at most 10). */
  filters: z.string().max(4000).optional(),
});
export const DocumentIndexRow = z.object({
  id: Uuid,
  title: z.string(),
  icon: z.string().nullable(),
  folderId: Uuid,
  parentDocumentId: Uuid.nullable(),
  path: z.string(),
  locked: z.boolean(),
  properties: z.record(z.string(), CustomFieldValue),
  updatedAt: IsoDateTime,
  updatedBy: UserRef.nullable(),
});
export type DocumentIndexRow = z.infer<typeof DocumentIndexRow>;

export const DocumentDraftView = z.object({
  title: z.string(),
  content: DocumentContent,
  /** The content revision the draft started from. */
  baseVersion: z.number().int(),
  /** The document's title or content changed after the draft started. */
  stale: z.boolean(),
  updatedAt: IsoDateTime,
});
export type DocumentDraftView = z.infer<typeof DocumentDraftView>;
export const SaveDocumentDraft = z.object({ title: Title, content: DocumentContent, baseVersion: Version }).strict();
export type SaveDocumentDraft = z.infer<typeof SaveDocumentDraft>;
export const PublishDocumentDraft = z.object({ force: z.boolean().optional() }).strict();
export type PublishDocumentDraft = z.infer<typeof PublishDocumentDraft>;

export const TranslationLocale = z.enum(['en', 'pt-BR', 'es']);
export const DocumentTranslationQuery = z.object({ locale: TranslationLocale });
export const DocumentTranslationView = z.object({
  locale: TranslationLocale,
  /** READY: every block translated; PENDING: some still on their way; UNAVAILABLE: translation is off or capped. */
  status: z.enum(['READY', 'PENDING', 'UNAVAILABLE']),
  content: DocumentContent,
  translated: z.number().int(),
  pending: z.number().int(),
  failed: z.number().int(),
  /** DISABLED: no translator; DAILY_LIMIT / RATE_LIMIT: new blocks wait; INVALID: the translation could not be shown. */
  reason: z.enum(['DISABLED', 'DAILY_LIMIT', 'RATE_LIMIT', 'INVALID']).nullable(),
});
export type DocumentTranslationView = z.infer<typeof DocumentTranslationView>;


// ---------------------------------------------------------------- Public links (ADR-0040 §5, ADR-0056)

/** The violation code of a change that would publish documents without `confirmPublic`. */
export const PUBLIC_CONFIRMATION_REQUIRED = 'public_confirmation_required';

/**
 * The field code of a folder change refused because a folder inside the "Public external" tree would
 * restrict its viewers (setting access, creating one, or moving a restricted folder in). Everything in
 * that tree is published by link, so restricting a subfolder would not hide it from outsiders.
 */
export const PUBLIC_TREE_RESTRICTED = 'public_tree_restricted';

/** A public link token: 32 random bytes in base64url (256 bits), never a document id. */
export const PublicLinkToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const PublicTokenParam = z.object({ token: PublicLinkToken });

/** Why a document of the public folder is not public right now. */
export const PublicLinkBlocker = z.enum(['OUTSIDE_PUBLIC_FOLDER', 'LOCKED', 'ARCHIVED']);

/** A document's public link, for its editors. The password itself is never returned. */
export const DocumentPublicLinkView = z.object({
  /** Whether anonymous public links are on in this deployment (DOCUMENTS_PUBLIC_LINKS). */
  enabled: z.boolean(),
  /** Anyone with the link reads the document now (ignoring `enabled`). */
  public: z.boolean(),
  reason: PublicLinkBlocker.nullable(),
  /** The web path, `/p/<token>`, when the document is public and links are on. */
  path: z.string().nullable(),
  /** The full address on the web app, when `path` is set. */
  url: z.string().nullable(),
  hasPassword: z.boolean(),
  passwordUpdatedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime.nullable(),
  rotatedAt: IsoDateTime.nullable(),
  /** Anonymous views per day (UTC), with no viewer identity. */
  views: z.object({ total: z.number().int(), last30Days: z.number().int(), days: z.array(z.object({ day: z.string(), count: z.number().int() })) }),
});
export type DocumentPublicLinkView = z.infer<typeof DocumentPublicLinkView>;

export const SetPublicLinkPassword = z.object({ password: z.string().min(8).max(200) }).strict();
export type SetPublicLinkPassword = z.infer<typeof SetPublicLinkPassword>;
export const UnlockPublicDocument = z.object({ password: z.string().min(1).max(200) }).strict();
export type UnlockPublicDocument = z.infer<typeof UnlockPublicDocument>;

/**
 * What an anonymous reader gets for a public link: the read-only content, or a request for the
 * password. Comments, history, authors and edit controls are never part of it; internal mentions
 * and links are already reduced to plain text, and media points at short-lived signed addresses.
 */
export const PublicDocumentView = z.discriminatedUnion('state', [
  z.object({ state: z.literal('PASSWORD_REQUIRED') }),
  z.object({
    state: z.literal('OPEN'),
    document: z.object({ title: z.string(), icon: z.string().nullable(), content: DocumentContent, originalLocale: z.string(), updatedAt: IsoDateTime }),
  }),
]);
export type PublicDocumentView = z.infer<typeof PublicDocumentView>;
